<template>
    <kiosk-layout formContainer>
        <template v-slot:title>
            <span class="inline-flex items-center gap-3">
                <font-awesome-icon widthAuto :icon="faFileLines" class="text-base"></font-awesome-icon>
                <span>New Terms &amp; Conditions</span>
            </span>
        </template>

        <form-wrapper :sections="sections">
            <term-form-title id="title" v-model="data" :canEdit="true" />
            <term-form-description id="description" v-model="data" :canEdit="true" />
            <term-form-content id="content" v-model="data" :canEdit="true" />
            <term-form-effective-at id="effective-at" v-model="data" :canEdit="true" />
            <term-form-org-unit-coverage id="org-units" v-model="data" :teamId="props.teamId" :canEdit="true" />
            <term-form-consent id="consent" v-model="data" :canEdit="true" />

            <vx-sticky-footer>
                <vx-button :href="backHref" class="w-20" size="lg" color="muted">Cancel</vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="processing"
                >
                    Create Terms
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { VxButton, VxStickyFooter } from '@voxie/frontend-components';
import { computed, onMounted, ref, watch } from 'vue';
import { faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FormWrapper from '~/components/general/FormWrapper.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import TermFormTitle from './TermFormTitle.vue';
import TermFormDescription from './TermFormDescription.vue';
import TermFormContent from './TermFormContent.vue';
import TermFormEffectiveAt from './TermFormEffectiveAt.vue';
import TermFormOrgUnitCoverage from './TermFormOrgUnitCoverage.vue';
import TermFormConsent from './TermFormConsent.vue';
import { useTermFormSubmission } from './useTermFormSubmission';
import { useTeamDetails } from '~/components/kiosk/kiosk-teams/composables/useTeamDetails';
import type { Term } from '~/services/adminTermsClient.types';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{
    teamId: number;
}>();

const toasted = useToasted();

const { data, submit, processing, backHref, loadActiveTerm } = useTermFormSubmission(props.teamId, null);
const { team, loading: teamLoading } = useTeamDetails();
const activeTerm = ref<Term | null>(null);

const hasOrgUnits = computed(
    () => !teamLoading.value && team.value.id === props.teamId && Boolean(team.value.team_attributes?.contact_groups)
);

const sections = computed(() => [
    { id: 'title', label: 'Title' },
    { id: 'description', label: 'Description' },
    { id: 'content', label: 'Content' },
    { id: 'effective-at', label: 'Effective' },
    ...(hasOrgUnits.value ? [{ id: 'org-units', label: 'Organizational Units' }] : []),
    { id: 'consent', label: 'Consent' },
]);

watch([activeTerm, hasOrgUnits], ([term, enabled]) => {
    if (term && enabled && data.value.org_unit_coverage === 'none' && data.value.org_units.length === 0) {
        data.value.org_unit_coverage = term.org_unit_coverage;
        data.value.org_units = term.org_units ?? [];
    }
});

onMounted(() => {
    loadActiveTerm()
        .then((term) => {
            activeTerm.value = term;
        })
        .catch((error: unknown) => toasted.error(catchRequestError(error)));
});
</script>
